/**
 * Laboratorio dental. La clínica envía órdenes de trabajo a sus laboratorios (internos o externos), sigue su estado en un tablero, chatea,
 * comparte fotos y enlaces de descarga y lleva las cuentas. El laboratorio entra con su propio usuario y solo ve sus órdenes.
 */
import { imprimir as imprimirHoja } from "../imprimir.ts";
import { useEffect, useMemo, useState } from "react";
import { api, apiBlob, puede, fijarClinicaLab } from "../api.ts";
import {
  TIPOS_TRABAJO, GRUPOS_TRABAJO, ARCADAS, NOMBRE_ESTADO_ORDEN, tipoTrabajo, camposDeHoja, resumenDatos, hojaVacia, dinero, leerMonto, esAbierta,
  type CampoHoja, type HojaLaboratorio, type EstadoOrden,
} from "../../../dominio/src/index.ts";
import { AvisoError, Seccion, useAccion } from "./comun.tsx";
import { BuscarPaciente } from "./Club.tsx";
import { preparar } from "./Fotos.tsx";
import { InvitacionesRecibidas } from "../componentes/Invitaciones.tsx";
import { FechaInput } from "../componentes/CampoFecha.tsx";

/* ---------------- tipos que devuelve la API ---------------- */
interface LabFila {
  id: string; nombre: string; interno: boolean; telefono: string; correo: string; direccion: string; tipos: string[]; hoja: HojaLaboratorio; acepta_garantia: boolean;
  correo_acceso: string; nota: string; activo: boolean; acceso: { nombre: string; email: string; activo: boolean; ultimoAcceso: string | null } | null; invitado: boolean; abiertas: number; clinica?: string;
  /** el laboratorio con su propia Donna (no una fila dentro de una clínica) y si quien consulta es su propietario */
  propia?: boolean; propietario?: boolean;
}
interface OrdenFila {
  id: string; numero: number; laboratorio_id: string; laboratorio: string; interno: boolean; paciente_id?: string; paciente_nombre: string; tipo: string; piezas: number[]; arcada: string;
  color: string; indicaciones: string; urgente: boolean; fecha_entrega: string | null; datos: Record<string, unknown>; estado: EstadoOrden; caso_id?: string | null; rehace_de: string | null;
  garantia: boolean; motivo_rehacer: string; costo: number | null; entregada_en: string | null; creado: string; actualizado: string; creado_por: string; atrasada: boolean;
}
interface Evento { estado: string; nota: string; lado: string; por_nombre: string; creado: string }
interface Mensaje { id: string; texto: string; lado: string; por_nombre: string; creado: string }
interface Enlace { id: string; url: string; nombre: string; lado: string; por_nombre: string; creado: string; mio: boolean }
interface FotoO { id: string; nombre: string; lado: string; por_nombre: string; creado: string; mia: boolean; miniatura: string }
interface Detalle extends OrdenFila {
  campos: CampoHoja[]; hoja?: HojaLaboratorio; tipoNombre: string; aceptaGarantia: boolean; eventos: Evento[]; mensajes: Mensaje[]; enlaces: Enlace[]; fotos: FotoO[];
  siguientes: { clave: EstadoOrden; nombre: string }[]; puedeResponderPrueba: boolean; puedeRehacer: boolean;
}
interface Plantilla { id: string; laboratorio_id: string; nombre: string; tipo: string; color: string; indicaciones: string; datos: Record<string, unknown> }

/* ---------------- ayudas ---------------- */
const hora = (iso: string) => new Date(iso).toLocaleString("es-EC", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "America/Guayaquil" });
const dia = (f: string) => new Date(f.slice(0, 10) + "T12:00:00").toLocaleDateString("es-EC", { day: "numeric", month: "short", year: "numeric" });
const piezasTexto = (o: { piezas: number[]; arcada: string }) => (o.piezas.length ? o.piezas.join(", ") : o.arcada ? `arcada ${o.arcada}` : "—");
const chipEstado = (o: { estado: string; atrasada?: boolean }) => (o.atrasada ? "rojo" : ["prueba_en_boca", "ajuste"].includes(o.estado) ? "azul" : "");
const dolares = (c: number | null) => (c === null ? "—" : c === 0 ? "sin costo" : dinero(c));

function ListaChips({ lista, valor, alElegir, varias = false, bloqueado = false }: { lista: readonly string[]; valor: string | string[] | undefined; alElegir: (v: string) => void; varias?: boolean; bloqueado?: boolean }) {
  const activo = (x: string) => (varias ? Array.isArray(valor) && valor.includes(x) : valor === x);
  return <div className="chipset">{lista.map((x) => <button key={x} type="button" className="tog" disabled={bloqueado} aria-pressed={activo(x)} onClick={() => alElegir(x)}>{x}</button>)}</div>;
}

function CampoHojaInput({ c, valor, cambiar }: { c: CampoHoja; valor: unknown; cambiar: (v: unknown) => void }) {
  if (c.tipo === "opcion") return <div className="campo">{c.etiqueta}{c.requerido ? " *" : ""}<ListaChips lista={c.opciones ?? []} valor={typeof valor === "string" ? valor : ""} alElegir={(x) => cambiar(valor === x ? "" : x)} /></div>;
  if (c.tipo === "varias") {
    const act = Array.isArray(valor) ? (valor as string[]) : [];
    return <div className="campo">{c.etiqueta}<ListaChips varias lista={c.opciones ?? []} valor={act} alElegir={(x) => cambiar(act.includes(x) ? act.filter((y) => y !== x) : [...act, x])} /></div>;
  }
  if (c.tipo === "si_no") return <label className="campo">{c.etiqueta}<select value={valor === true ? "si" : valor === false ? "no" : ""} onChange={(e) => cambiar(e.target.value === "" ? "" : e.target.value === "si")}><option value="">—</option><option value="si">Sí</option><option value="no">No</option></select></label>;
  return <label className="campo">{c.etiqueta}<input value={typeof valor === "string" ? valor : ""} maxLength={200} onChange={(e) => cambiar(e.target.value)} /></label>;
}

/* ---------------- formulario de la orden ---------------- */
function FormOrden({ labs, paciente, inicial, alGuardar, alCancelar }: {
  labs: LabFila[]; paciente?: { id: string; nombre: string } | null; inicial?: Detalle; alGuardar: (o: OrdenFila) => void; alCancelar: () => void;
}) {
  const activos = labs.filter((l) => l.activo);
  const [pac, setPac] = useState<{ id: string; nombre: string } | null>(paciente ?? (inicial ? { id: inicial.paciente_id!, nombre: inicial.paciente_nombre } : null));
  const [labId, setLabId] = useState(inicial?.laboratorio_id ?? (activos.length === 1 ? activos[0].id : ""));
  const [tipo, setTipo] = useState(inicial?.tipo ?? "");
  const [piezasTxt, setPiezasTxt] = useState(inicial ? inicial.piezas.join(", ") : "");
  const [arcada, setArcada] = useState(inicial?.arcada ?? "");
  const [color, setColor] = useState(inicial?.color ?? "");
  const [indicaciones, setIndicaciones] = useState(inicial?.indicaciones ?? "");
  const [urgente, setUrgente] = useState(inicial?.urgente ?? false);
  const [fecha, setFecha] = useState(inicial?.fecha_entrega ?? "");
  const [datos, setDatos] = useState<Record<string, unknown>>(inicial?.datos ?? {});
  const [casoId, setCasoId] = useState("");
  const [casos, setCasos] = useState<{ id: string; etiqueta: string }[]>([]);
  const [plantillas, setPlantillas] = useState<Plantilla[]>([]);
  const [nombrePlantilla, setNombrePlantilla] = useState("");
  const [aviso, setAviso] = useState("");
  const a = useAccion();
  const lab = labs.find((l) => l.id === labId) ?? null;
  const t = tipoTrabajo(tipo);
  const permitidos = lab ? TIPOS_TRABAJO.filter((x) => !lab.tipos.length || lab.tipos.includes(x.clave)) : [];
  const campos = t && lab ? camposDeHoja(t.clave, lab.hoja ?? hojaVacia()) : [];
  const sobreImplantes = !!t && ["Sobre implantes"].includes(t.grupo) || ["guia_quirurgica", "sobredentadura"].includes(tipo);

  useEffect(() => { if (tipo && lab && !permitidos.some((x) => x.clave === tipo)) setTipo(""); }, [labId]);
  useEffect(() => { if (labId) api<Plantilla[]>("GET", `/api/laboratorio/plantillas?laboratorio=${labId}`).then(setPlantillas).catch(() => setPlantillas([])); else setPlantillas([]); }, [labId]);
  useEffect(() => {
    setCasos([]); setCasoId("");
    if (!pac || !sobreImplantes) return;
    api<{ casos: { id: string; estado: string; plan?: { implantes?: { pieza: number }[] } }[] }>("GET", `/api/pacientes/${pac.id}/implantes`)
      .then((r) => setCasos(r.casos.filter((c) => c.estado !== "fracaso").map((c) => ({ id: c.id, etiqueta: `Implantes en ${(c.plan?.implantes ?? []).map((i) => i.pieza).join(", ") || "—"}${c.estado === "en_curso" ? " (en curso)" : ""}` }))))
      .catch(() => undefined);
  }, [pac?.id, sobreImplantes]);

  async function usarCaso(id: string) {
    setCasoId(id);
    if (!id) return;
    const r = await a.correr(() => api<{ piezas: number[]; datos: Record<string, unknown> }>("GET", `/api/laboratorio/desde-caso/${id}`));
    if (!r) return;
    if (t && t.piezas !== "arcada" && t.piezas !== "ninguna") setPiezasTxt(r.piezas.join(", "));
    const validos = new Set(campos.map((c) => c.clave));
    setDatos((d) => ({ ...d, ...Object.fromEntries(Object.entries(r.datos).filter(([k]) => validos.has(k))) }));
    setAviso("Se llenaron las piezas, el sistema de implante y la retención con lo planificado. Revísalos.");
  }
  function aplicarPlantilla(id: string) {
    const p = plantillas.find((x) => x.id === id);
    if (!p) return;
    setTipo(p.tipo); setColor(p.color); setIndicaciones(p.indicaciones); setDatos(p.datos);
  }
  const piezas = piezasTxt.split(/[\s,;]+/).filter(Boolean).map(Number);

  async function guardar() {
    const cuerpo = { tipo, piezas: t?.piezas === "arcada" || t?.piezas === "ninguna" ? [] : piezas, arcada: t?.piezas === "arcada" ? arcada : "", color, indicaciones, urgente, fechaEntrega: fecha || null, datos };
    const r = await a.correr(() => inicial
      ? api<OrdenFila>("PUT", `/api/laboratorio/ordenes/${inicial.id}`, cuerpo)
      : api<OrdenFila>("POST", "/api/laboratorio/ordenes", { ...cuerpo, laboratorioId: labId, pacienteId: pac!.id, ...(casoId ? { casoId } : {}) }));
    if (r) alGuardar(r);
  }
  async function guardarPlantilla() {
    if (!nombrePlantilla.trim() || !t) return;
    const r = await a.correr(() => api<Plantilla>("POST", "/api/laboratorio/plantillas", { laboratorioId: labId, nombre: nombrePlantilla, tipo, color, indicaciones, datos }));
    if (r) { setPlantillas((l) => [...l.filter((x) => x.id !== r.id), r]); setNombrePlantilla(""); setAviso("Plantilla guardada."); }
  }
  const listo = !!pac && !!lab && !!t && (t.piezas === "arcada" ? !!arcada : t.piezas === "ninguna" ? true : piezas.length > 0);

  return (
    <div className="panel-accion" style={{ gap: 12 }}>
      <h3 style={{ margin: 0 }}>{inicial ? `Modificar la orden N.º ${inicial.numero}` : "Nueva orden de trabajo"}</h3>
      <AvisoError error={a.error} />
      {aviso && <div className="aviso ok" role="status">{aviso}</div>}
      {!inicial && (pac
        ? <div className="fila"><span className="chip">Paciente</span><b>{pac.nombre}</b>{!paciente && <button className="enlace" onClick={() => setPac(null)}>Cambiar</button>}</div>
        : <BuscarPaciente etiqueta="Paciente" alElegir={(p) => setPac({ id: p.id, nombre: `${p.nombres} ${p.apellidos}`.trim() })} />)}
      {!inicial && (
        <label className="campo">Laboratorio
          <select value={labId} onChange={(e) => setLabId(e.target.value)}>
            <option value="">Elige el laboratorio…</option>
            {activos.map((l) => <option key={l.id} value={l.id}>{l.nombre}{l.interno ? " (interno)" : ""}</option>)}
          </select>
        </label>
      )}
      {!activos.length && <div className="aviso info">Primero registra un laboratorio en la pestaña «Laboratorios».</div>}
      {lab && (
        <>
          {!inicial && plantillas.length > 0 && (
            <label className="campo">Usar una plantilla
              <select value="" onChange={(e) => aplicarPlantilla(e.target.value)}><option value="">Elige una…</option>{plantillas.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}</select>
            </label>
          )}
          <label className="campo">Tipo de trabajo
            <select value={tipo} onChange={(e) => { setTipo(e.target.value); setDatos({}); }}>
              <option value="">Elige el trabajo…</option>
              {GRUPOS_TRABAJO.map((g) => { const items = permitidos.filter((x) => x.grupo === g); return items.length ? <optgroup key={g} label={g}>{items.map((x) => <option key={x.clave} value={x.clave}>{x.nombre}</option>)}</optgroup> : null; })}
            </select>
          </label>
          {t && (
            <>
              {sobreImplantes && casos.length > 0 && (
                <label className="campo">Tomar los datos de un caso de implantes
                  <select value={casoId} onChange={(e) => usarCaso(e.target.value)}><option value="">No usar</option>{casos.map((c) => <option key={c.id} value={c.id}>{c.etiqueta}</option>)}</select>
                </label>
              )}
              {t.piezas === "arcada"
                ? <div className="campo">Arcada *<ListaChips lista={ARCADAS} valor={arcada} alElegir={(x) => setArcada(arcada === x ? "" : x)} /></div>
                : t.piezas !== "ninguna" && <label className="campo">Piezas (como en el odontograma, separadas por coma) *<input className="mono" value={piezasTxt} onChange={(e) => setPiezasTxt(e.target.value)} placeholder="Ej.: 36, 37" inputMode="numeric" /></label>}
              <div className="fila" style={{ gap: 12, alignItems: "flex-end" }}>
                <label className="campo" style={{ flex: "1 1 160px" }}>Color / tono<input value={color} maxLength={40} onChange={(e) => setColor(e.target.value)} placeholder="Ej.: A2 (Vita Classical)" /></label>
                <label className="campo" style={{ flex: "1 1 160px" }}>Fecha de entrega<FechaInput value={fecha} onChange={(e) => setFecha(e.target.value)} /></label>
                <label className="fila" style={{ gap: 6 }}><input type="checkbox" checked={urgente} onChange={(e) => setUrgente(e.target.checked)} /> Urgente</label>
              </div>
              {campos.length > 0 && <fieldset className="checklist"><legend>Hoja de datos de {lab.nombre}</legend><div style={{ display: "grid", gap: 10 }}>{campos.map((c) => <CampoHojaInput key={c.clave} c={c} valor={datos[c.clave]} cambiar={(v) => setDatos({ ...datos, [c.clave]: v })} />)}</div></fieldset>}
              <label className="campo">Indicaciones para el técnico<textarea value={indicaciones} maxLength={1000} onChange={(e) => setIndicaciones(e.target.value)} placeholder="Contactos, oclusión, forma, lo que no puede faltar…" /></label>
              <p className="muted" style={{ margin: 0, fontSize: 13 }}>Los escaneos y archivos pesados se comparten con un enlace de descarga (por ejemplo WeTransfer) desde la orden, una vez enviada. El laboratorio solo verá el nombre del paciente.</p>
            </>
          )}
        </>
      )}
      <div className="fila" style={{ gap: 8 }}>
        <button className="btn pri" disabled={a.ocupado || !listo} onClick={guardar}>{inicial ? "Guardar cambios" : "Enviar al laboratorio"}</button>
        <button className="btn" onClick={alCancelar}>Cancelar</button>
        {t && lab && (
          <span className="fila" style={{ gap: 6, marginLeft: "auto" }}>
            <input className="entrada" style={{ width: 170 }} placeholder="Nombre de plantilla" value={nombrePlantilla} onChange={(e) => setNombrePlantilla(e.target.value)} aria-label="Nombre de la plantilla" />
            <button className="btn chico" disabled={a.ocupado || !nombrePlantilla.trim()} onClick={guardarPlantilla}>Guardar como plantilla</button>
          </span>
        )}
      </div>
    </div>
  );
}

/* ---------------- detalle de la orden ---------------- */
function Foto({ id, miniatura, nombre }: { id: string; miniatura: string; nombre: string }) {
  const [grande, setGrande] = useState("");
  const a = useAccion();
  return (
    <>
      <button className="mini" onClick={async () => { const u = await a.correr(() => apiBlob(`/api/laboratorio/fotos/${id}/imagen`)); if (u) setGrande(u); }} aria-label={`Ver la foto ${nombre}`}><img src={miniatura} alt="" /></button>
      {grande && <div role="dialog" aria-label={nombre || "Foto"} onClick={() => setGrande("")} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.8)", display: "grid", placeItems: "center", zIndex: 50, padding: 16 }}><img src={grande} alt={nombre} style={{ maxWidth: "100%", maxHeight: "100%", borderRadius: 8 }} /></div>}
    </>
  );
}

function DetalleOrden({ id, labs, lado, alCambiar, alCerrar, clinicaId }: { id: string; labs: LabFila[]; lado: "clinica" | "laboratorio"; alCambiar: () => void; alCerrar: () => void; clinicaId?: string }) {
  const [o, setO] = useState<Detalle | null>(null);
  const [nota, setNota] = useState("");
  const [texto, setTexto] = useState("");
  const [url, setUrl] = useState("");
  const [nombreEnlace, setNombreEnlace] = useState("");
  const [costo, setCosto] = useState("");
  const [motivo, setMotivo] = useState("");
  const [modo, setModo] = useState<"" | "editar" | "rehacer" | "ajustar">("");
  const [ok, setOk] = useState("");
  const a = useAccion();
  const cargar = async () => { const r = await a.correr(() => api<Detalle>("GET", `/api/laboratorio/ordenes/${id}`)); if (r) { setO(r); setCosto(r.costo ? String(r.costo / 100) : ""); } };
  // un laboratorio con varias clínicas: todo lo que haga con esta orden va a la clínica que la envió
  useEffect(() => { fijarClinicaLab(clinicaId ?? null); return () => fijarClinicaLab(null); }, [clinicaId]);
  useEffect(() => { void cargar(); }, [id, clinicaId]);
  if (!o) return <AvisoError error={a.error} />;
  const clinica = lado === "clinica";
  const admin = puede("laboratorio:admin");
  const escribe = puede("laboratorio:escribir");
  const cerrada = o.estado === "cancelado" || o.estado === "instalado";

  const accion = async (fn: () => Promise<unknown>, mensaje = "") => { const r = await a.correr(fn); if (r !== undefined) { setOk(mensaje); await cargar(); alCambiar(); } return r; };
  async function subirFotos(archivos: FileList | null) {
    if (!archivos?.length) return;
    for (const f of Array.from(archivos)) {
      const r = await a.correr(async () => { const x = await preparar(f); return api("POST", `/api/laboratorio/ordenes/${id}/fotos`, { nombre: f.name.replace(/\.[^.]+$/, "").slice(0, 100), ...x }); });
      if (r === undefined) break;
    }
    await cargar();
  }
  const resumen = resumenDatos(o.tipo, o.hoja ?? labs.find((l) => l.id === o.laboratorio_id)?.hoja ?? hojaVacia(), o.datos);

  return (
    <section className="card orden-detalle" aria-label={`Orden ${o.numero}`}>
      <div className="fila" style={{ justifyContent: "space-between" }}>
        <h2 style={{ margin: 0 }}>Orden N.º {o.numero} · {o.tipoNombre}</h2>
        <span className="fila" style={{ gap: 6 }}>
          <button className="btn chico" onClick={() => imprimirHoja()}>Imprimir hoja</button>
          <button className="btn chico" onClick={alCerrar}>Cerrar</button>
        </span>
      </div>
      <AvisoError error={a.error} />
      {ok && <div className="aviso ok" role="status">{ok}</div>}
      <div className="fila" style={{ gap: 8 }}>
        <span className={`chip ${chipEstado(o)}`}>{NOMBRE_ESTADO_ORDEN[o.estado]}</span>
        {o.atrasada && <span className="chip rojo">Atrasada</span>}
        {o.urgente && <span className="chip rojo">Urgente</span>}
        {o.garantia && <span className="chip azul">Garantía</span>}
        {o.rehace_de && <span className="chip">Repetición</span>}
        <span className="muted">{o.laboratorio}{o.interno ? " (interno)" : ""}</span>
      </div>
      <dl className="datos-orden">
        <div><dt>Paciente</dt><dd>{o.paciente_nombre}</dd></div>
        <div><dt>Piezas</dt><dd className="mono">{piezasTexto(o)}</dd></div>
        {o.color && <div><dt>Color</dt><dd>{o.color}</dd></div>}
        <div><dt>Entrega pedida</dt><dd>{o.fecha_entrega ? dia(o.fecha_entrega) : "—"}</dd></div>
        <div><dt>Pedida por</dt><dd>{o.creado_por} · {hora(o.creado)}</dd></div>
        {o.entregada_en && <div><dt>Entregada</dt><dd>{hora(o.entregada_en)}</dd></div>}
        {o.costo !== null && <div><dt>Costo del trabajo</dt><dd>{dolares(o.costo)}</dd></div>}
        {o.motivo_rehacer && <div style={{ gridColumn: "1 / -1" }}><dt>Motivo de la repetición</dt><dd>{o.motivo_rehacer}</dd></div>}
        {resumen.map((r) => <div key={r.etiqueta}><dt>{r.etiqueta}</dt><dd>{r.valor}</dd></div>)}
        {o.indicaciones && <div style={{ gridColumn: "1 / -1" }}><dt>Indicaciones</dt><dd style={{ whiteSpace: "pre-wrap" }}>{o.indicaciones}</dd></div>}
      </dl>

      {modo === "editar" && clinica && (
        <FormOrden labs={labs} inicial={o} alGuardar={async () => { setModo(""); setOk("Orden modificada. El laboratorio ve el cambio en el historial."); await cargar(); alCambiar(); }} alCancelar={() => setModo("")} />
      )}

      {/* lo que se puede hacer ahora */}
      <div className="fila" style={{ gap: 8, alignItems: "flex-end" }}>
        {o.siguientes.length > 0 && !o.puedeResponderPrueba && (
          <>
            <label className="campo" style={{ flex: "1 1 200px" }}>Nota (opcional)<input value={nota} maxLength={500} onChange={(e) => setNota(e.target.value)} /></label>
            {o.siguientes.map((s) => (
              <button key={s.clave} className={`btn ${s.clave === "cancelado" ? "" : "pri"}`} disabled={a.ocupado}
                onClick={() => { if (s.clave === "cancelado" && !confirm("¿Cancelar esta orden?")) return; void accion(async () => { await api("POST", `/api/laboratorio/ordenes/${id}/estado`, { estado: s.clave, nota }); setNota(""); return true; }, `Orden: ${s.nombre.toLowerCase()}.`); }}>
                {s.clave === "cancelado" ? "Cancelar orden" : `Marcar «${s.nombre}»`}
              </button>
            ))}
          </>
        )}
        {clinica && escribe && ["enviado", "recibido", "en_proceso", "ajuste"].includes(o.estado) && <button className="btn" onClick={() => setModo(modo === "editar" ? "" : "editar")}>Modificar la orden</button>}
        {clinica && escribe && o.puedeRehacer && <button className="btn" onClick={() => setModo(modo === "rehacer" ? "" : "rehacer")}>Pedir que lo rehagan</button>}
      </div>

      {o.puedeResponderPrueba && clinica && (
        <div className="panel-accion">
          <b>Prueba en boca: ¿cómo quedó?</b>
          <label className="campo">Nota (obligatoria si hay que ajustar)<textarea value={nota} maxLength={500} onChange={(e) => setNota(e.target.value)} placeholder="Ej.: el contacto proximal con la 35 está flojo" /></label>
          <div className="fila" style={{ gap: 8 }}>
            <button className="btn pri" disabled={a.ocupado} onClick={() => accion(async () => { await api("POST", `/api/laboratorio/ordenes/${id}/prueba`, { resultado: "aprobada", nota }); setNota(""); return true; }, "Prueba aprobada: el laboratorio puede terminar el trabajo.")}>Aprobada</button>
            <button className="btn" disabled={a.ocupado || !nota.trim()} onClick={() => accion(async () => { await api("POST", `/api/laboratorio/ordenes/${id}/prueba`, { resultado: "ajustar", nota }); setNota(""); return true; }, "Se pidió el ajuste al laboratorio.")}>Hay que ajustar</button>
          </div>
        </div>
      )}
      {modo === "rehacer" && clinica && (
        <div className="panel-accion">
          <b>Pedir que lo rehagan</b>
          <label className="campo">¿Qué pasó?<textarea value={motivo} maxLength={500} onChange={(e) => setMotivo(e.target.value)} placeholder="Ej.: fractura de la porcelana a los 15 días" /></label>
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>{o.aceptaGarantia ? "Este laboratorio acepta la garantía: la repetición no se cobra." : "Este laboratorio no tiene garantía activada: te dirá si cobra la repetición."}</p>
          <button className="btn pri" disabled={a.ocupado || !motivo.trim()} onClick={async () => { const r = await a.correr(() => api<OrdenFila>("POST", `/api/laboratorio/ordenes/${id}/rehacer`, { motivo })); if (r) { setModo(""); setMotivo(""); alCambiar(); setOk(`Se creó la orden N.º ${r.numero} (repetición).`); } }}>Enviar la repetición</button>
        </div>
      )}

      {/* costo: lo fija el laboratorio o la administración */}
      {(!clinica || admin) && !cerrada && (
        <div className="fila" style={{ gap: 8, alignItems: "flex-end" }}>
          <label className="campo" style={{ maxWidth: 200 }}>Costo del trabajo (USD)<input className="mono" inputMode="decimal" value={costo} onChange={(e) => setCosto(e.target.value)} placeholder="0.00" /></label>
          <button className="btn chico" disabled={a.ocupado || leerMonto(costo || "0") === null} onClick={() => accion(async () => { await api("POST", `/api/laboratorio/ordenes/${id}/costo`, { costo: leerMonto(costo || "0") }); return true; }, "Costo guardado.")}>Guardar costo</button>
        </div>
      )}

      {/* fotos y enlaces */}
      <div>
        <b>Fotos del caso y del trabajo</b>
        <div className="fila" style={{ gap: 8, margin: "6px 0" }}>
          {o.fotos.map((f) => (
            <span key={f.id} className="foto-orden" title={`${f.por_nombre} · ${hora(f.creado)}`}>
              <Foto id={f.id} miniatura={f.miniatura} nombre={f.nombre} />
              {(f.mia || admin) && <button className="enlace" style={{ fontSize: 12 }} onClick={() => accion(async () => { await api("DELETE", `/api/laboratorio/fotos/${f.id}`); return true; })}>Quitar</button>}
              <small className="muted">{f.lado === "clinica" ? "Clínica" : "Laboratorio"}</small>
            </span>
          ))}
          {!o.fotos.length && <span className="muted">Sin fotos.</span>}
        </div>
        {!cerrada && <label className="btn chico" style={{ cursor: "pointer" }}>Subir fotos<input type="file" accept="image/*" multiple hidden onChange={(e) => { void subirFotos(e.target.files); e.target.value = ""; }} /></label>}
      </div>
      <div>
        <b>Enlaces de descarga</b>
        <p className="muted" style={{ margin: "2px 0 6px", fontSize: 13 }}>Para escaneos (STL, PLY, OBJ), diseños de exocad y archivos pesados: súbelos a WeTransfer u otro servicio y pega aquí el enlace de descarga. Donna lo muestra a la otra parte y le avisa.</p>
        {o.enlaces.map((e) => (
          <div key={e.id} className="fila" style={{ gap: 8 }}>
            <a href={e.url} target="_blank" rel="noopener noreferrer">{e.nombre || e.url}</a>
            <small className="muted">{e.lado === "clinica" ? "Clínica" : "Laboratorio"} · {e.por_nombre} · {hora(e.creado)}</small>
            {(e.mio || admin) && <button className="enlace" onClick={() => accion(async () => { await api("DELETE", `/api/laboratorio/enlaces/${e.id}`); return true; })}>Quitar</button>}
          </div>
        ))}
        {!o.enlaces.length && <span className="muted">Sin enlaces.</span>}
        {!cerrada && (
          <div className="fila" style={{ gap: 8, marginTop: 6 }}>
            <input className="entrada" style={{ flex: "2 1 220px" }} placeholder="https://we.tl/…" value={url} onChange={(e) => setUrl(e.target.value)} aria-label="Enlace de descarga" />
            <input className="entrada" style={{ flex: "1 1 140px" }} placeholder="Qué contiene (opcional)" value={nombreEnlace} maxLength={120} onChange={(e) => setNombreEnlace(e.target.value)} aria-label="Descripción del enlace" />
            <button className="btn chico" disabled={a.ocupado || !url.trim()} onClick={() => accion(async () => { await api("POST", `/api/laboratorio/ordenes/${id}/enlaces`, { url, nombre: nombreEnlace }); setUrl(""); setNombreEnlace(""); return true; }, "Enlace agregado y avisado.")}>Agregar enlace</button>
          </div>
        )}
      </div>

      {/* chat */}
      <div>
        <b>Chat con {clinica ? "el laboratorio" : "la clínica"}</b>
        <div className="chat-orden" role="log" aria-live="polite">
          {o.mensajes.map((m) => <div key={m.id} className={`burbuja ${m.lado === lado ? "mia" : ""}`}><small className="muted">{m.por_nombre} · {hora(m.creado)}</small><div style={{ whiteSpace: "pre-wrap" }}>{m.texto}</div></div>)}
          {!o.mensajes.length && <span className="muted">Aún no hay mensajes.</span>}
        </div>
        <div className="fila" style={{ gap: 8 }}>
          <input className="entrada" style={{ flex: "1 1 240px" }} value={texto} maxLength={2000} placeholder="Escribe un mensaje…" onChange={(e) => setTexto(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && texto.trim()) void accion(async () => { await api("POST", `/api/laboratorio/ordenes/${id}/mensajes`, { texto }); setTexto(""); return true; }); }} aria-label="Mensaje" />
          <button className="btn chico pri" disabled={a.ocupado || !texto.trim() || (clinica && !escribe)} onClick={() => accion(async () => { await api("POST", `/api/laboratorio/ordenes/${id}/mensajes`, { texto }); setTexto(""); return true; })}>Enviar</button>
        </div>
      </div>

      <details>
        <summary>Historial de estados</summary>
        <ul className="lista">{o.eventos.map((e, i) => <li key={i} style={{ padding: "6px 4px", borderBottom: "1px solid var(--line)" }}><b>{NOMBRE_ESTADO_ORDEN[e.estado as EstadoOrden] ?? e.estado}</b> · {e.lado === "clinica" ? "Clínica" : "Laboratorio"} ({e.por_nombre}) · <span className="muted">{hora(e.creado)}</span>{e.nota && <div className="muted">{e.nota}</div>}</li>)}</ul>
      </details>

      {/* hoja para imprimir */}
      <div className="solo-impresion hoja-laboratorio">
        <h1>Orden de trabajo N.º {o.numero}</h1>
        <p><b>{o.tipoNombre}</b> · Laboratorio: {o.laboratorio}</p>
        <table><tbody>
          <tr><th>Paciente</th><td>{o.paciente_nombre}</td><th>Entrega pedida</th><td>{o.fecha_entrega ? dia(o.fecha_entrega) : "—"}</td></tr>
          <tr><th>Piezas</th><td>{piezasTexto(o)}</td><th>Color</th><td>{o.color || "—"}</td></tr>
          {resumen.map((r) => <tr key={r.etiqueta}><th>{r.etiqueta}</th><td colSpan={3}>{r.valor}</td></tr>)}
          {o.indicaciones && <tr><th>Indicaciones</th><td colSpan={3}>{o.indicaciones}</td></tr>}
          {o.urgente && <tr><th>Urgente</th><td colSpan={3}>Sí</td></tr>}
        </tbody></table>
        <p style={{ marginTop: 24 }}>Pedida por {o.creado_por} el {hora(o.creado)}.</p>
        <div className="fila" style={{ justifyContent: "space-between", marginTop: 40 }}><span>Firma del odontólogo: ____________________</span><span>Recibido por el laboratorio: ____________________</span></div>
      </div>
    </section>
  );
}

/* ---------------- tablero y lista ---------------- */
const COLUMNAS: EstadoOrden[] = ["enviado", "recibido", "en_proceso", "prueba_en_boca", "ajuste", "terminado", "entregado"];

function Tarjeta({ o, alAbrir, ver, color, clinica }: { o: OrdenFila; alAbrir: () => void; ver: "clinica" | "laboratorio"; color?: string; clinica?: string }) {
  return (
    <button className={`tarjeta-orden ${o.atrasada ? "atrasada" : ""}`} onClick={alAbrir} style={color ? { borderLeft: `6px solid ${color}`, background: `color-mix(in srgb, ${color} 10%, var(--surface))` } : undefined}>
      {clinica && <small style={{ color, fontWeight: 600 }}>{clinica}</small>}
      <span className="fila" style={{ justifyContent: "space-between", gap: 4 }}><b className="mono">N.º {o.numero}</b>{o.urgente && <span className="chip rojo">Urgente</span>}</span>
      <span>{o.paciente_nombre}</span>
      <small className="muted">{tipoTrabajo(o.tipo)?.nombre ?? o.tipo} · {piezasTexto(o)}</small>
      {ver === "clinica" && <small className="muted">{o.laboratorio}</small>}
      {o.fecha_entrega && <small className={o.atrasada ? "rojo-texto" : "muted"}>Entrega {dia(o.fecha_entrega)}{o.atrasada ? " · atrasada" : ""}</small>}
      {o.rehace_de && <small className="muted">Repetición{o.garantia ? " (garantía)" : ""}</small>}
    </button>
  );
}

function Ordenes({ labs, lado, pacienteInicial, nuevaInicial }: { labs: LabFila[]; lado: "clinica" | "laboratorio"; pacienteInicial?: { id: string; nombre: string } | null; nuevaInicial?: boolean }) {
  const [lista, setLista] = useState<OrdenFila[] | null>(null);
  const [vista, setVista] = useState<"tablero" | "lista">("tablero");
  const [filtroLab, setFiltroLab] = useState("");
  const [q, setQ] = useState("");
  const [verCerradas, setVerCerradas] = useState(false);
  const [abierta, setAbierta] = useState<string | null>(null);
  const [nueva, setNueva] = useState(!!nuevaInicial && lado === "clinica");
  const [resumen, setResumen] = useState<{ abiertas: number; atrasadas: number; porProbar: number } | null>(null);
  const a = useAccion();
  const cargar = async () => {
    const params = new URLSearchParams();
    if (filtroLab && lado === "clinica") params.set("laboratorio", filtroLab);
    if (pacienteInicial && lado === "clinica") params.set("paciente", pacienteInicial.id);
    if (q.trim()) params.set("q", q.trim());
    const r = await a.correr(() => api<OrdenFila[]>("GET", `/api/laboratorio/ordenes?${params}`));
    if (r) setLista(r);
    api<typeof resumen>("GET", "/api/laboratorio/resumen").then(setResumen).catch(() => undefined);
  };
  useEffect(() => { const t = setTimeout(() => { void cargar(); }, q ? 250 : 0); return () => clearTimeout(t); }, [filtroLab, q]);
  const visibles = useMemo(() => (lista ?? []).filter((o) => verCerradas || esAbierta(o.estado) || o.estado === "entregado"), [lista, verCerradas]);
  const escribe = puede("laboratorio:escribir");

  return (
    <>
      {lado === "clinica" && nueva && <FormOrden labs={labs} paciente={pacienteInicial ?? null} alGuardar={(o) => { setNueva(false); setAbierta(o.id); void cargar(); }} alCancelar={() => setNueva(false)} />}
      {abierta && <DetalleOrden key={abierta} id={abierta} labs={labs} lado={lado} alCambiar={() => { void cargar(); }} alCerrar={() => setAbierta(null)} />}
      <Seccion titulo={pacienteInicial ? `Órdenes de ${pacienteInicial.nombre}` : "Órdenes de trabajo"} acciones={lado === "clinica" && escribe && !nueva && <button className="btn pri" onClick={() => setNueva(true)}>Nueva orden</button>}>
        <AvisoError error={a.error} />
        {resumen && !pacienteInicial && (
          <div className="fila" style={{ gap: 8, flexWrap: "wrap" }}>
            <div className="card" style={{ padding: 10, minWidth: 130 }}><div className="muted" style={{ fontSize: 12 }}>Abiertas</div><b style={{ fontSize: 20 }}>{resumen.abiertas}</b></div>
            <div className="card" style={{ padding: 10, minWidth: 130 }}><div className="muted" style={{ fontSize: 12 }}>Atrasadas</div><b style={{ fontSize: 20, color: resumen.atrasadas ? "var(--red)" : undefined }}>{resumen.atrasadas}</b></div>
            {lado === "clinica" && <div className="card" style={{ padding: 10, minWidth: 130 }}><div className="muted" style={{ fontSize: 12 }}>Para probar en boca</div><b style={{ fontSize: 20 }}>{resumen.porProbar}</b></div>}
          </div>
        )}
        <div className="fila" style={{ gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
          <label className="campo" style={{ flex: "1 1 200px" }}>Buscar<input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Paciente o número de orden" /></label>
          {lado === "clinica" && !pacienteInicial && labs.length > 1 && <label className="campo" style={{ flex: "0 1 200px" }}>Laboratorio<select value={filtroLab} onChange={(e) => setFiltroLab(e.target.value)}><option value="">Todos</option>{labs.map((l) => <option key={l.id} value={l.id}>{l.nombre}</option>)}</select></label>}
          <span className="seg" role="group" aria-label="Vista"><button aria-pressed={vista === "tablero"} onClick={() => setVista("tablero")}>Tablero</button><button aria-pressed={vista === "lista"} onClick={() => setVista("lista")}>Lista</button></span>
          <label className="fila" style={{ gap: 6 }}><input type="checkbox" checked={verCerradas} onChange={(e) => setVerCerradas(e.target.checked)} /> Ver instaladas y canceladas</label>
        </div>
        {lista && !visibles.length && <p className="muted">{lado === "clinica" ? "Todavía no hay órdenes. Pulsa «Nueva orden» para enviar el primer trabajo a un laboratorio." : "Todavía no te han enviado órdenes."}</p>}
        {vista === "tablero" && visibles.length > 0 && (
          <div className="tablero-lab">
            {(verCerradas ? [...COLUMNAS, "instalado", "cancelado"] as EstadoOrden[] : COLUMNAS).map((e) => {
              const items = visibles.filter((o) => o.estado === e);
              return (
                <div key={e} className="columna-lab">
                  <h3>{NOMBRE_ESTADO_ORDEN[e]} <span className="muted">{items.length}</span></h3>
                  {items.map((o) => <Tarjeta key={o.id} o={o} ver={lado} alAbrir={() => setAbierta(o.id)} />)}
                </div>
              );
            })}
          </div>
        )}
        {vista === "lista" && visibles.length > 0 && (
          <div style={{ overflowX: "auto" }}>
            <table className="tabla-resp">
              <thead><tr><th>N.º</th><th>Paciente</th><th>Trabajo</th><th>Piezas</th>{lado === "clinica" && <th>Laboratorio</th>}<th>Entrega</th><th>Estado</th></tr></thead>
              <tbody>{visibles.map((o) => (
                <tr key={o.id}>
                  <td className="mono">{o.numero}</td><td><button className="enlace" onClick={() => setAbierta(o.id)}>{o.paciente_nombre}</button></td>
                  <td>{tipoTrabajo(o.tipo)?.nombre ?? o.tipo}</td><td className="mono">{piezasTexto(o)}</td>{lado === "clinica" && <td>{o.laboratorio}</td>}
                  <td>{o.fecha_entrega ? dia(o.fecha_entrega) : "—"}</td><td><span className={`chip ${chipEstado(o)}`}>{NOMBRE_ESTADO_ORDEN[o.estado]}</span></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </Seccion>
    </>
  );
}

/* ---------------- laboratorios: registro, acceso y hoja de datos ---------------- */
function EditorHoja({ tipos, hoja, cambiar }: { tipos: string[]; hoja: HojaLaboratorio; cambiar: (t: string[], h: HojaLaboratorio) => void }) {
  const [abierto, setAbierto] = useState<string | null>(null);
  const [nuevo, setNuevo] = useState<{ etiqueta: string; tipo: "opcion" | "varias" | "texto" | "si_no"; opciones: string }>({ etiqueta: "", tipo: "opcion", opciones: "" });
  const alternar = (c: string) => cambiar(tipos.includes(c) ? tipos.filter((x) => x !== c) : [...tipos, c], hoja);
  const ocultar = (clave: string) => cambiar(tipos, { ...hoja, ocultar: hoja.ocultar.includes(clave) ? hoja.ocultar.filter((x) => x !== clave) : [...hoja.ocultar, clave] });
  return (
    <div style={{ display: "grid", gap: 10 }}>
      <b>Trabajos con los que trabaja</b>
      <p className="muted" style={{ margin: 0, fontSize: 13 }}>Marca solo los trabajos que hace este laboratorio. Si no marcas ninguno, se ofrecen todos. Su hoja de datos sale de lo que marque.</p>
      {GRUPOS_TRABAJO.map((g) => (
        <fieldset key={g} className="checklist"><legend>{g}</legend>
          <div className="chipset">{TIPOS_TRABAJO.filter((t) => t.grupo === g).map((t) => (
            <span key={t.clave} className="fila" style={{ gap: 2 }}>
              <button type="button" className="tog" aria-pressed={tipos.includes(t.clave)} onClick={() => alternar(t.clave)}>{t.nombre}</button>
              {tipos.includes(t.clave) && <button type="button" className="enlace" style={{ fontSize: 12 }} onClick={() => setAbierto(abierto === t.clave ? null : t.clave)} aria-expanded={abierto === t.clave}>Preguntas</button>}
            </span>
          ))}</div>
          {TIPOS_TRABAJO.filter((t) => t.grupo === g && abierto === t.clave).map((t) => (
            <div key={t.clave} className="panel-accion" style={{ marginTop: 6 }}>
              <b>Preguntas de «{t.nombre}»</b>
              <p className="muted" style={{ margin: 0, fontSize: 13 }}>Desmarca las que este laboratorio no necesita. Las obligatorias (*) no se pueden quitar.</p>
              {t.campos.map((c) => <label key={c.clave} className="fila" style={{ gap: 6 }}><input type="checkbox" disabled={c.requerido} checked={!hoja.ocultar.includes(`${t.clave}.${c.clave}`)} onChange={() => ocultar(`${t.clave}.${c.clave}`)} /> {c.etiqueta}{c.requerido ? " *" : ""}</label>)}
            </div>
          ))}
        </fieldset>
      ))}
      <b>Preguntas propias</b>
      {hoja.extras.map((e, i) => (
        <div key={i} className="fila" style={{ gap: 8 }}>
          <span><b>{e.etiqueta}</b> <span className="muted">({e.tipo === "opcion" ? "una opción" : e.tipo === "varias" ? "varias opciones" : e.tipo === "si_no" ? "sí o no" : "texto"}{e.opciones ? `: ${e.opciones.join(", ")}` : ""})</span></span>
          <button className="enlace" onClick={() => cambiar(tipos, { ...hoja, extras: hoja.extras.filter((_, j) => j !== i) })}>Quitar</button>
        </div>
      ))}
      <div className="fila" style={{ gap: 8, alignItems: "flex-end" }}>
        <label className="campo" style={{ flex: "2 1 200px" }}>Pregunta<input value={nuevo.etiqueta} maxLength={80} onChange={(e) => setNuevo({ ...nuevo, etiqueta: e.target.value })} placeholder="Ej.: Marca de la cerámica" /></label>
        <label className="campo" style={{ flex: "1 1 140px" }}>Tipo<select value={nuevo.tipo} onChange={(e) => setNuevo({ ...nuevo, tipo: e.target.value as typeof nuevo.tipo })}><option value="opcion">Una opción</option><option value="varias">Varias opciones</option><option value="texto">Texto</option><option value="si_no">Sí o no</option></select></label>
        {(nuevo.tipo === "opcion" || nuevo.tipo === "varias") && <label className="campo" style={{ flex: "2 1 200px" }}>Opciones (separadas por coma)<input value={nuevo.opciones} onChange={(e) => setNuevo({ ...nuevo, opciones: e.target.value })} placeholder="Ivoclar, Vita" /></label>}
        <button className="btn chico" disabled={!nuevo.etiqueta.trim() || ((nuevo.tipo === "opcion" || nuevo.tipo === "varias") && !nuevo.opciones.trim())}
          onClick={() => { cambiar(tipos, { ...hoja, extras: [...hoja.extras, { clave: `x_${hoja.extras.length + 1}`, etiqueta: nuevo.etiqueta.trim(), tipo: nuevo.tipo, ...(nuevo.tipo === "opcion" || nuevo.tipo === "varias" ? { opciones: nuevo.opciones.split(",").map((x) => x.trim()).filter(Boolean) } : {}) }] }); setNuevo({ etiqueta: "", tipo: "opcion", opciones: "" }); }}>Agregar pregunta</button>
      </div>
    </div>
  );
}

function FormLab({ inicial, alGuardar, alCancelar, propio = false }: { inicial?: LabFila; alGuardar: (l: LabFila) => void; alCancelar?: () => void; propio?: boolean }) {
  const [nombre, setNombre] = useState(inicial?.nombre ?? "");
  const [interno, setInterno] = useState(inicial?.interno ?? false);
  const [telefono, setTelefono] = useState(inicial?.telefono ?? "");
  const [correo, setCorreo] = useState(inicial?.correo ?? "");
  const [direccion, setDireccion] = useState(inicial?.direccion ?? "");
  const [nota, setNota] = useState(inicial?.nota ?? "");
  const [garantia, setGarantia] = useState(inicial?.acepta_garantia ?? false);
  const [activo, setActivo] = useState(inicial?.activo ?? true);
  const [tipos, setTipos] = useState<string[]>(inicial?.tipos ?? []);
  const [hoja, setHoja] = useState<HojaLaboratorio>(inicial?.hoja ?? hojaVacia());
  const [hojaAbierta, setHojaAbierta] = useState(!inicial || propio);
  const a = useAccion();
  async function guardar() {
    const cuerpo = { nombre, interno, telefono, correo, direccion, nota, aceptaGarantia: garantia, activo, tipos, hoja };
    const r = await a.correr(() => propio ? api<LabFila>("PUT", "/api/laboratorio/mi", cuerpo) : inicial ? api<LabFila>("PUT", `/api/laboratorios/${inicial.id}`, cuerpo) : api<LabFila>("POST", "/api/laboratorios", cuerpo));
    if (r) alGuardar(r);
  }
  return (
    <div className="panel-accion" style={{ gap: 12 }}>
      {!propio && <h3 style={{ margin: 0 }}>{inicial ? `Editar ${inicial.nombre}` : "Nuevo laboratorio"}</h3>}
      <AvisoError error={a.error} />
      <div className="fila" style={{ gap: 12 }}>
        {(!propio || inicial?.propia) && <label className="campo" style={{ flex: "2 1 240px" }}>Nombre del laboratorio *<input value={nombre} maxLength={120} onChange={(e) => setNombre(e.target.value)} /></label>}
        <label className="campo" style={{ flex: "1 1 160px" }}>Teléfono / WhatsApp<input value={telefono} maxLength={30} onChange={(e) => setTelefono(e.target.value)} /></label>
        <label className="campo" style={{ flex: "2 1 220px" }}>Correo (para avisos)<input type="email" value={correo} maxLength={120} onChange={(e) => setCorreo(e.target.value)} /></label>
      </div>
      <label className="campo">Dirección<input value={direccion} maxLength={200} onChange={(e) => setDireccion(e.target.value)} /></label>
      {!propio && <label className="fila" style={{ gap: 6 }}><input type="checkbox" checked={interno} onChange={(e) => setInterno(e.target.checked)} /> Laboratorio interno (el técnico trabaja en la propia clínica)</label>}
      <label className="fila" style={{ gap: 6 }}><input type="checkbox" checked={garantia} onChange={(e) => setGarantia(e.target.checked)} /> Acepta garantía: repite sin costo un trabajo con problema</label>
      {!propio && <label className="campo">Nota interna<input value={nota} maxLength={300} onChange={(e) => setNota(e.target.value)} /></label>}
      {inicial && !propio && <label className="fila" style={{ gap: 6 }}><input type="checkbox" checked={activo} onChange={(e) => setActivo(e.target.checked)} /> Activo (si lo desactivas ya no se le envían órdenes nuevas)</label>}
      <button className="enlace" style={{ justifySelf: "start" }} onClick={() => setHojaAbierta(!hojaAbierta)} aria-expanded={hojaAbierta}>{hojaAbierta ? "Ocultar" : "Elegir"} los trabajos y la hoja de datos</button>
      {hojaAbierta && <EditorHoja tipos={tipos} hoja={hoja} cambiar={(t, h) => { setTipos(t); setHoja(h); }} />}
      <div className="fila" style={{ gap: 8 }}>
        <button className="btn pri" disabled={a.ocupado || !nombre.trim()} onClick={guardar}>Guardar</button>
        {alCancelar && <button className="btn" onClick={alCancelar}>Cancelar</button>}
      </div>
    </div>
  );
}

function AccesoLab({ lab, alCambiar }: { lab: LabFila; alCambiar: () => void }) {
  const [nombre, setNombre] = useState(lab.nombre);
  const [email, setEmail] = useState(lab.correo);
  const [clave, setClave] = useState("");
  const [aviso, setAviso] = useState("");
  const a = useAccion();
  async function dar() {
    const r = await a.correr(() => api<{ invitada: boolean; claveTemporal?: string; mensaje?: string; email?: string }>("POST", `/api/laboratorios/${lab.id}/acceso`, { nombre, email }));
    if (!r) return;
    if (r.invitada) setAviso(r.mensaje ?? "Invitación enviada.");
    else setClave(r.claveTemporal ?? "");
    alCambiar();
  }
  if (lab.acceso) return <p className="muted" style={{ margin: 0 }}>Entra con <b>{lab.acceso.email}</b>{lab.acceso.ultimoAcceso ? ` · último acceso ${hora(lab.acceso.ultimoAcceso)}` : " · todavía no ha entrado"}. Si olvidó la contraseña, restablécela en Configuración → Usuarios.</p>;
  if (lab.invitado) return <p className="muted" style={{ margin: 0 }}>Invitación enviada a <b>{lab.correo_acceso}</b>. Está esperando que el laboratorio la acepte al iniciar sesión.</p>;
  return (
    <div className="panel-accion">
      <b>Dar acceso a Donna a este laboratorio</b>
      <p className="muted" style={{ margin: 0, fontSize: 13 }}>El laboratorio entra con su propio usuario y ve solo las órdenes que le envíes (el nombre del paciente y los datos del trabajo, nada más). Si esa persona ya usa Donna en otra clínica, recibe una invitación y la acepta ella misma.</p>
      {clave ? (
        <div className="aviso ok" role="status">Usuario creado. Entrégale esta contraseña temporal (se muestra una sola vez): <b className="mono">{clave}</b><br />Su correo para entrar es {email}. Al entrar tendrá que cambiarla.</div>
      ) : aviso ? <div className="aviso ok" role="status">{aviso}</div> : (
        <div className="fila" style={{ gap: 8, alignItems: "flex-end" }}>
          <label className="campo" style={{ flex: "1 1 200px" }}>Nombre de quien entra<input value={nombre} maxLength={80} onChange={(e) => setNombre(e.target.value)} /></label>
          <label className="campo" style={{ flex: "1 1 220px" }}>Correo<input type="email" value={email} maxLength={120} onChange={(e) => setEmail(e.target.value)} /></label>
          <button className="btn pri" disabled={a.ocupado || nombre.trim().length < 3 || !email.trim()} onClick={dar}>Dar acceso</button>
        </div>
      )}
      <AvisoError error={a.error} />
    </div>
  );
}

function Laboratorios({ labs, recargar }: { labs: LabFila[]; recargar: () => void }) {
  const [editando, setEditando] = useState<LabFila | "nuevo" | null>(null);
  return (
    <Seccion titulo="Mis laboratorios" acciones={!editando && <button className="btn pri" onClick={() => setEditando("nuevo")}>Nuevo laboratorio</button>}>
      {editando && <FormLab inicial={editando === "nuevo" ? undefined : editando} alGuardar={() => { setEditando(null); recargar(); }} alCancelar={() => setEditando(null)} />}
      {!labs.length && !editando && <p className="muted">Registra el laboratorio con el que trabajas (interno o externo). Después podrás enviarle órdenes y darle acceso para que las vea y las actualice.</p>}
      {labs.map((l) => (
        <div key={l.id} className="card" style={{ boxShadow: "none", opacity: l.activo ? 1 : 0.6 }}>
          <div className="fila" style={{ justifyContent: "space-between" }}>
            <span><b>{l.nombre}</b> {l.interno && <span className="chip">Interno</span>} {!l.activo && <span className="chip rojo">Desactivado</span>} {l.acepta_garantia && <span className="chip azul">Garantía</span>}</span>
            <button className="btn chico" onClick={() => setEditando(l)}>Editar</button>
          </div>
          <small className="muted">{[l.telefono, l.correo, l.direccion].filter(Boolean).join(" · ") || "Sin datos de contacto"} · {l.abiertas} orden{l.abiertas === 1 ? "" : "es"} abierta{l.abiertas === 1 ? "" : "s"}</small>
          <small className="muted">Trabaja: {l.tipos.length ? l.tipos.map((t) => tipoTrabajo(t)?.nombre ?? t).join(", ") : "todos los trabajos (aún no eligió)"}</small>
          <AccesoLab lab={l} alCambiar={recargar} />
        </div>
      ))}
    </Seccion>
  );
}

/* ---------------- cuentas y estadísticas ---------------- */
interface Cuenta {
  clinicaId?: string; clinica?: string; laboratorioId: string; laboratorio: string; interno: boolean; debe: number; pagado: number; saldo: number; enProceso: number;
  cierre: { mes: string; saldoAnterior: number; trabajos: number; entregado: number; pagado: number; saldoFinal: number };
  pagos: { id: string; monto: number; fecha: string; forma: string; nota: string; anulado: boolean }[];
}
/** Los últimos 24 meses (y el elegido, si es más antiguo), de más nuevo a más viejo, como aaaa-mm. */
function mesesRecientes(elegido: string): string[] {
  const ahora = new Date(Date.now() - 5 * 3600 * 1000);
  const out: string[] = [];
  for (let i = 0; i < 24; i++) { const d = new Date(Date.UTC(ahora.getUTCFullYear(), ahora.getUTCMonth() - i, 1)); out.push(d.toISOString().slice(0, 7)); }
  return out.includes(elegido) ? out : [...out, elegido];
}
const nombreMes = (m: string) => { const t = new Date(m + "-01T12:00:00Z").toLocaleDateString("es-EC", { month: "long", year: "numeric", timeZone: "UTC" }); return t[0].toUpperCase() + t.slice(1); };
const mesActual = () => new Date(Date.now() - 5 * 3600 * 1000).toISOString().slice(0, 7);

function Cuentas({ lado }: { lado: "clinica" | "laboratorio" }) {
  const [mes, setMes] = useState(mesActual());
  const [d, setD] = useState<Cuenta[] | null>(null);
  const [pagando, setPagando] = useState<string | null>(null);
  const [monto, setMonto] = useState("");
  const [forma, setForma] = useState("Transferencia");
  const [notaPago, setNotaPago] = useState("");
  const a = useAccion();
  const admin = puede("laboratorio:admin") && lado === "clinica";
  const cargar = async () => {
    if (lado === "laboratorio") { const r = await a.correr(() => api<{ cuentas: Cuenta[] }>("GET", `/api/laboratorio/mis-cuentas?mes=${mes}`)); if (r) setD(r.cuentas); return; }
    const r = await a.correr(() => api<{ laboratorios: Cuenta[] }>("GET", `/api/laboratorio/cuentas?mes=${mes}`)); if (r) setD(r.laboratorios);
  };
  useEffect(() => { void cargar(); }, [mes]);
  if (!d) return <AvisoError error={a.error} />;
  return (
    <Seccion titulo={lado === "clinica" ? "Cuentas por pagar a los laboratorios" : "Mi cuenta con cada clínica"} acciones={<label className="fila" style={{ gap: 6 }}>Mes del cierre <select value={mes} onChange={(e) => setMes(e.target.value)}>{mesesRecientes(mes).map((m) => <option key={m} value={m}>{nombreMes(m)}</option>)}</select></label>}>
      <AvisoError error={a.error} />
      <p className="muted" style={{ margin: 0, fontSize: 13 }}>Se debe lo que ya está entregado o instalado y tiene costo. Las repeticiones en garantía no se cobran. Los pagos equivocados se anulan, no se borran.</p>
      {!d.length && <p className="muted">Todavía no hay trabajos con costo.</p>}
      {d.map((c) => (
        <div key={c.laboratorioId} className="card" style={{ boxShadow: "none" }}>
          <div className="fila" style={{ justifyContent: "space-between" }}>
            <b>{c.clinica ?? c.laboratorio}{c.clinica ? "" : c.interno ? " (interno)" : ""}</b>
            {admin && <button className="btn chico" onClick={() => setPagando(pagando === c.laboratorioId ? null : c.laboratorioId)}>Registrar un pago</button>}
          </div>
          <div className="fila" style={{ gap: 8, flexWrap: "wrap" }}>
            {([["Entregado (se debe)", c.debe], ["Pagado", c.pagado], ["Saldo pendiente", c.saldo], ["En proceso", c.enProceso]] as [string, number][]).map(([t, v]) => (
              <div key={t} className="card" style={{ padding: 10, minWidth: 150 }}><div className="muted" style={{ fontSize: 12 }}>{t}</div><b style={{ fontSize: 18, color: t.startsWith("Saldo") && v > 0 ? "var(--red)" : undefined }}>{dinero(v)}</b></div>
            ))}
          </div>
          <small className="muted">Cierre de {mes}: saldo anterior {dinero(c.cierre.saldoAnterior)} + {c.cierre.trabajos} trabajo{c.cierre.trabajos === 1 ? "" : "s"} entregado{c.cierre.trabajos === 1 ? "" : "s"} {dinero(c.cierre.entregado)} − pagos {dinero(c.cierre.pagado)} = <b>{dinero(c.cierre.saldoFinal)}</b></small>
          {pagando === c.laboratorioId && (
            <div className="panel-accion">
              <div className="fila" style={{ gap: 8, alignItems: "flex-end" }}>
                <label className="campo">Monto (USD)<input className="mono" inputMode="decimal" value={monto} onChange={(e) => setMonto(e.target.value)} /></label>
                <label className="campo">Forma<input value={forma} maxLength={40} onChange={(e) => setForma(e.target.value)} /></label>
                <label className="campo" style={{ flex: "1 1 160px" }}>Nota<input value={notaPago} maxLength={300} onChange={(e) => setNotaPago(e.target.value)} /></label>
                <button className="btn pri" disabled={a.ocupado || !leerMonto(monto)} onClick={async () => { const r = await a.correr(() => api("POST", "/api/laboratorio/pagos", { laboratorioId: c.laboratorioId, monto: leerMonto(monto), forma, nota: notaPago })); if (r) { setPagando(null); setMonto(""); setNotaPago(""); await cargar(); } }}>Guardar pago</button>
              </div>
            </div>
          )}
          {c.pagos.length > 0 && (
            <details><summary>Pagos ({c.pagos.length})</summary>
              <ul className="lista">{c.pagos.map((p) => (
                <li key={p.id} style={{ padding: "6px 4px", borderBottom: "1px solid var(--line)", textDecoration: p.anulado ? "line-through" : undefined }}>
                  {dia(p.fecha)} · <b>{dinero(p.monto)}</b> {p.forma && `· ${p.forma}`} {p.nota && `· ${p.nota}`} {p.anulado && <span className="chip">Anulado</span>}
                  {admin && !p.anulado && <> <button className="enlace" onClick={async () => { if (confirm("¿Anular este pago?") && await a.correr(() => api("POST", `/api/laboratorio/pagos/${p.id}/anular`))) await cargar(); }}>Anular</button></>}
                </li>
              ))}</ul>
            </details>
          )}
        </div>
      ))}
    </Seccion>
  );
}

interface Estadistica { laboratorioId: string; laboratorio: string; interno: boolean; total: number; entregadas: number; diasPromedio: number | null; puntualidad: number | null; repeticiones: number; porcentajeRepeticion: number; costoTotal: number; atrasadas: number; porTipo: { tipo: string; nombre: string; n: number }[] }

function Estadisticas() {
  const [d, setD] = useState<Estadistica[] | null>(null);
  const a = useAccion();
  useEffect(() => { void a.correr(async () => setD(await api<Estadistica[]>("GET", "/api/laboratorio/reportes"))); }, []);
  if (!d) return <AvisoError error={a.error} />;
  return (
    <Seccion titulo="Cómo trabaja cada laboratorio">
      <p className="muted" style={{ margin: 0, fontSize: 13 }}>Tiempo desde que se envía la orden hasta que se entrega, puntualidad frente a la fecha pedida, repeticiones y costo total. Sirve para comparar proveedores.</p>
      {!d.length && <p className="muted">Todavía no hay datos.</p>}
      <div style={{ overflowX: "auto" }}>
        {d.length > 0 && (
          <table className="tabla-resp">
            <thead><tr><th>Laboratorio</th><th>Órdenes</th><th>Entregadas</th><th>Días promedio</th><th>Puntualidad</th><th>Repeticiones</th><th>Atrasadas</th><th>Costo total</th><th>Más pedidos</th></tr></thead>
            <tbody>{d.map((x) => (
              <tr key={x.laboratorioId}>
                <td><b>{x.laboratorio}</b>{x.interno ? " (interno)" : ""}</td><td>{x.total}</td><td>{x.entregadas}</td><td>{x.diasPromedio ?? "—"}</td><td>{x.puntualidad === null ? "—" : `${x.puntualidad} %`}</td>
                <td>{x.repeticiones} ({x.porcentajeRepeticion} %)</td><td style={{ color: x.atrasadas ? "var(--red)" : undefined }}>{x.atrasadas}</td><td>{dinero(x.costoTotal)}</td>
                <td>{x.porTipo.slice(0, 3).map((t) => `${t.nombre} (${t.n})`).join(", ") || "—"}</td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </div>
    </Seccion>
  );
}

/* ---------------- pantallas ---------------- */
function useLabs() {
  const [labs, setLabs] = useState<LabFila[] | null>(null);
  const a = useAccion();
  const recargar = async () => { const r = await a.correr(() => api<LabFila[]>("GET", "/api/laboratorios")); if (r) setLabs(r); };
  useEffect(() => { void recargar(); }, []);
  return { labs, recargar, error: a.error };
}

/** Pantalla de la clínica. */
export function Laboratorio({ pacienteInicial, nuevaInicial }: { pacienteInicial?: { id: string; nombre: string } | null; nuevaInicial?: boolean }) {
  const { labs, recargar, error } = useLabs();
  const admin = puede("laboratorio:admin");
  const [pestana, setPestana] = useState<"ordenes" | "laboratorios" | "cuentas" | "estadisticas">("ordenes");
  if (!labs) return <AvisoError error={error} />;
  return (
    <>
      <h1 className="titulo-pagina">Laboratorio</h1>
      <nav className="pestanas" role="tablist">
        {([["ordenes", "Órdenes", true], ["laboratorios", "Laboratorios", admin], ["cuentas", "Cuentas", admin], ["estadisticas", "Estadísticas", admin]] as [typeof pestana, string, boolean][]).filter((x) => x[2]).map(([k, t]) => (
          <button key={k} role="tab" aria-selected={pestana === k} onClick={() => setPestana(k)}>{t}</button>
        ))}
      </nav>
      {pestana === "ordenes" && <Ordenes labs={labs} lado="clinica" pacienteInicial={pacienteInicial} nuevaInicial={nuevaInicial} />}
      {pestana === "laboratorios" && admin && <Laboratorios labs={labs} recargar={recargar} />}
      {pestana === "cuentas" && admin && <Cuentas lado="clinica" />}
      {pestana === "estadisticas" && admin && <Estadisticas />}
    </>
  );
}

/** Pestaña «Laboratorio» de la ficha del paciente. */
export function OrdenesPaciente({ paciente }: { paciente: { id: string; nombres: string; apellidos: string } }) {
  const { labs, error } = useLabs();
  if (!labs) return <AvisoError error={error} />;
  return <Ordenes labs={labs} lado="clinica" pacienteInicial={{ id: paciente.id, nombre: `${paciente.nombres} ${paciente.apellidos}`.trim() }} />;
}

/* ---------------- bandeja de entrada del laboratorio ---------------- */
interface ClinicaBandeja { clinicaId: string; clinica: string; laboratorio: string; color: string }
type OrdenBandeja = OrdenFila & { clinicaId: string };

function Bandeja() {
  const [d, setD] = useState<{ clinicas: ClinicaBandeja[]; ordenes: OrdenBandeja[] } | null>(null);
  const [vista, setVista] = useState<"tablero" | "lista">("tablero");
  const [soloClinica, setSoloClinica] = useState("");
  const [q, setQ] = useState("");
  const [verCerradas, setVerCerradas] = useState(false);
  const [abierta, setAbierta] = useState<OrdenBandeja | null>(null);
  const a = useAccion();
  const cargar = async () => { const r = await a.correr(() => api<{ clinicas: ClinicaBandeja[]; ordenes: OrdenBandeja[] }>("GET", "/api/laboratorio/bandeja")); if (r) setD(r); };
  useEffect(() => { void cargar(); }, []);
  // al aceptar la invitación de otra clínica, sus órdenes aparecen aquí
  useEffect(() => { const f = () => { void cargar(); }; addEventListener("donna:clinicas", f); return () => removeEventListener("donna:clinicas", f); }, []);
  if (!d) return <AvisoError error={a.error} />;
  const color = (id: string) => d.clinicas.find((c) => c.clinicaId === id)?.color ?? "#33475B";
  const nombre = (id: string) => d.clinicas.find((c) => c.clinicaId === id)?.clinica ?? "";
  const visibles = d.ordenes.filter((o) => (!soloClinica || o.clinicaId === soloClinica) && (verCerradas || esAbierta(o.estado) || o.estado === "entregado")
    && (!q.trim() || `${o.paciente_nombre} ${o.numero}`.toLowerCase().includes(q.trim().toLowerCase())));
  const abiertas = d.ordenes.filter((o) => esAbierta(o.estado)), atrasadas = abiertas.filter((o) => o.atrasada).length;
  async function elegirColor(clinicaId: string, c: string) {
    setD({ ...d!, clinicas: d!.clinicas.map((x) => (x.clinicaId === clinicaId ? { ...x, color: c } : x)) });
    await a.correr(() => api("PUT", "/api/laboratorio/color", { clinicaId, color: c }));
  }
  return (
    <>
      {abierta && <DetalleOrden key={abierta.id} id={abierta.id} clinicaId={abierta.clinicaId} labs={[]} lado="laboratorio" alCambiar={() => { void cargar(); }} alCerrar={() => setAbierta(null)} />}
      <Seccion titulo="Bandeja de entrada">
        <AvisoError error={a.error} />
        <div className="fila" style={{ gap: 8, flexWrap: "wrap" }}>
          <div className="card" style={{ padding: 10, minWidth: 130 }}><div className="muted" style={{ fontSize: 12 }}>Abiertas</div><b style={{ fontSize: 20 }}>{abiertas.length}</b></div>
          <div className="card" style={{ padding: 10, minWidth: 130 }}><div className="muted" style={{ fontSize: 12 }}>Nuevas por recibir</div><b style={{ fontSize: 20 }}>{abiertas.filter((o) => o.estado === "enviado").length}</b></div>
          <div className="card" style={{ padding: 10, minWidth: 130 }}><div className="muted" style={{ fontSize: 12 }}>Atrasadas</div><b style={{ fontSize: 20, color: atrasadas ? "var(--red)" : undefined }}>{atrasadas}</b></div>
        </div>
        {d.clinicas.length > 0 && (
          <div className="fila" style={{ gap: 8, flexWrap: "wrap" }} aria-label="Clínicas con las que trabajas">
            {d.clinicas.map((c) => (
              <span key={c.clinicaId} className="fila" style={{ gap: 4, border: "1px solid var(--line)", borderRadius: 999, padding: "2px 8px 2px 4px", background: soloClinica === c.clinicaId ? `color-mix(in srgb, ${c.color} 18%, var(--surface))` : "var(--surface)" }}>
                <input type="color" value={c.color} onChange={(e) => { void elegirColor(c.clinicaId, e.target.value); }} aria-label={`Color de ${c.clinica}`} title="Cambiar el color de esta clínica" style={{ width: 24, height: 24, padding: 0, border: 0, background: "none", cursor: "pointer" }} />
                <button className="enlace" style={{ textDecoration: "none", color: "inherit", fontWeight: soloClinica === c.clinicaId ? 700 : 500 }} aria-pressed={soloClinica === c.clinicaId}
                  onClick={() => setSoloClinica(soloClinica === c.clinicaId ? "" : c.clinicaId)} title="Ver solo las órdenes de esta clínica">{c.clinica} <span className="muted">({d.ordenes.filter((o) => o.clinicaId === c.clinicaId && esAbierta(o.estado)).length})</span></button>
              </span>
            ))}
          </div>
        )}
        <div className="fila" style={{ gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
          <label className="campo" style={{ flex: "1 1 200px" }}>Buscar<input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Paciente o número de orden" /></label>
          <span className="seg" role="group" aria-label="Vista"><button aria-pressed={vista === "tablero"} onClick={() => setVista("tablero")}>Tablero</button><button aria-pressed={vista === "lista"} onClick={() => setVista("lista")}>Lista</button></span>
          <label className="fila" style={{ gap: 6 }}><input type="checkbox" checked={verCerradas} onChange={(e) => setVerCerradas(e.target.checked)} /> Ver instaladas y canceladas</label>
        </div>
        {!d.clinicas.length && <p className="muted">Todavía no trabajas con ninguna clínica. Cuando una clínica te dé acceso (o te invite) verás aquí sus órdenes.</p>}
        {d.clinicas.length > 0 && !visibles.length && <p className="muted">No hay órdenes para mostrar.</p>}
        {vista === "tablero" && visibles.length > 0 && (
          <div className="tablero-lab">
            {(verCerradas ? [...COLUMNAS, "instalado", "cancelado"] as EstadoOrden[] : COLUMNAS).map((e) => {
              const items = visibles.filter((o) => o.estado === e);
              return (
                <div key={e} className="columna-lab">
                  <h3>{NOMBRE_ESTADO_ORDEN[e]} <span className="muted">{items.length}</span></h3>
                  {items.map((o) => <Tarjeta key={o.id} o={o} ver="laboratorio" color={color(o.clinicaId)} clinica={d.clinicas.length > 1 ? nombre(o.clinicaId) : undefined} alAbrir={() => setAbierta(o)} />)}
                </div>
              );
            })}
          </div>
        )}
        {vista === "lista" && visibles.length > 0 && (
          <div style={{ overflowX: "auto" }}>
            <table className="tabla-resp">
              <thead><tr><th>N.º</th>{d.clinicas.length > 1 && <th>Clínica</th>}<th>Paciente</th><th>Trabajo</th><th>Piezas</th><th>Entrega</th><th>Estado</th></tr></thead>
              <tbody>{visibles.map((o) => (
                <tr key={o.id} style={{ boxShadow: `inset 6px 0 0 ${color(o.clinicaId)}` }}>
                  <td className="mono">{o.numero}</td>{d.clinicas.length > 1 && <td><b style={{ color: color(o.clinicaId) }}>{nombre(o.clinicaId)}</b></td>}
                  <td><button className="enlace" onClick={() => setAbierta(o)}>{o.paciente_nombre}</button></td>
                  <td>{tipoTrabajo(o.tipo)?.nombre ?? o.tipo}</td><td className="mono">{piezasTexto(o)}</td>
                  <td>{o.fecha_entrega ? dia(o.fecha_entrega) : "—"}</td><td><span className={`chip ${chipEstado(o)}`}>{NOMBRE_ESTADO_ORDEN[o.estado]}</span></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </Seccion>
    </>
  );
}

/* ---------------- el laboratorio con su propia Donna ---------------- */
interface Invitacion { id: string; email: string; estado: string; creado: string; vence: string; vencida: boolean; clinica: string | null }

function ClinicasLab() {
  const [d, setD] = useState<{ invitaciones: Invitacion[]; clinicas: { clinica_id: string; clinica: string }[] } | null>(null);
  const [email, setEmail] = useState("");
  const [clinica, setClinica] = useState("");
  const [ok, setOk] = useState("");
  const a = useAccion();
  const cargar = async () => { const r = await a.correr(() => api<NonNullable<typeof d>>("GET", "/api/laboratorio/invitaciones-enviadas")); if (r) setD(r); };
  useEffect(() => { void cargar(); }, []);
  async function invitar() {
    const r = await a.correr(() => api<{ existe: boolean; enviado: boolean }>("POST", "/api/laboratorio/invitar-clinica", { email, clinica }));
    if (!r) return;
    const enlace = `${location.origin}/#/registro?tipo=clinica&correo=${encodeURIComponent(email)}`;
    setOk(r.enviado
      ? (r.existe ? "Invitación enviada: esa clínica ya usa Donna y la verá al iniciar sesión." : "Invitación enviada por correo: la clínica podrá crear su cuenta de Donna (15 días gratis) y verá tu invitación al entrar.")
      : `La invitación quedó registrada, pero no pudimos enviar el correo. Comparte este enlace con la clínica: ${enlace}`);
    setEmail(""); setClinica(""); await cargar();
  }
  if (!d) return <AvisoError error={a.error} />;
  const ESTADO: Record<string, string> = { pendiente: "Esperando respuesta", aceptada: "Aceptada", rechazada: "Rechazada", cancelada: "Cancelada" };
  return (
    <>
      <Seccion titulo="Invitar a una clínica">
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>Invita a las clínicas con las que trabajas para enviarse órdenes por Donna. Si la clínica ya usa Donna, acepta la invitación al iniciar sesión. Si no, le llega un correo para crear su cuenta (15 días de prueba gratis) y conectarse contigo.</p>
        <AvisoError error={a.error} />
        {ok && <div className="aviso ok" role="status">{ok}</div>}
        <div className="fila" style={{ gap: 8, alignItems: "flex-end" }}>
          <label className="campo" style={{ flex: "1 1 200px" }}>Nombre de la clínica (opcional)<input value={clinica} maxLength={120} onChange={(e) => setClinica(e.target.value)} /></label>
          <label className="campo" style={{ flex: "2 1 240px" }}>Correo de la clínica<input type="email" value={email} maxLength={120} onChange={(e) => setEmail(e.target.value)} placeholder="administracion@clinica.com" /></label>
          <button className="btn pri" disabled={a.ocupado || !email.trim()} onClick={invitar}>Enviar invitación</button>
        </div>
      </Seccion>
      <Seccion titulo="Clínicas conectadas">
        {!d.clinicas.length && <p className="muted">Todavía no hay clínicas conectadas. Cuando una acepte tu invitación aparecerá aquí y sus órdenes llegarán a tu bandeja.</p>}
        <ul className="lista">{d.clinicas.map((c) => <li key={c.clinica_id} style={{ padding: "8px 4px", borderBottom: "1px solid var(--line)" }}><b>{c.clinica}</b></li>)}</ul>
      </Seccion>
      {d.invitaciones.length > 0 && (
        <Seccion titulo="Invitaciones enviadas">
          <ul className="lista">{d.invitaciones.map((i) => (
            <li key={i.id} className="fila" style={{ padding: "8px 4px", borderBottom: "1px solid var(--line)", justifyContent: "space-between" }}>
              <span><b>{i.clinica ?? i.email}</b> {i.clinica && <span className="muted">({i.email})</span>} · <span className="muted">{i.vencida ? "Vencida" : ESTADO[i.estado] ?? i.estado} · {hora(i.creado)}</span></span>
              {i.estado === "pendiente" && !i.vencida && <button className="enlace" onClick={async () => { if (await a.correr(() => api("POST", `/api/laboratorio/invitaciones-enviadas/${i.id}/cancelar`))) await cargar(); }}>Cancelar</button>}
            </li>
          ))}</ul>
        </Seccion>
      )}
    </>
  );
}

interface Tecnico { id: string; nombre: string; email: string; activo: boolean; debe_cambiar: boolean; ultimo_acceso: string | null; propietario: boolean }

function EquipoLab() {
  const [lista, setLista] = useState<Tecnico[] | null>(null);
  const [nombre, setNombre] = useState("");
  const [email, setEmail] = useState("");
  const [clave, setClave] = useState<{ para: string; clave: string } | null>(null);
  const a = useAccion();
  const cargar = async () => { const r = await a.correr(() => api<Tecnico[]>("GET", "/api/laboratorio/equipo")); if (r) setLista(r); };
  useEffect(() => { void cargar(); }, []);
  if (!lista) return <AvisoError error={a.error} />;
  return (
    <Seccion titulo="Mi equipo de técnicos">
      <p className="muted" style={{ margin: 0, fontSize: 13 }}>Cada técnico entra con su propio correo y ve la misma bandeja de entrada. Sin límite de usuarios.</p>
      <AvisoError error={a.error} />
      {clave && <div className="aviso ok" role="status">Contraseña temporal de <b>{clave.para}</b> (se muestra una sola vez): <b className="mono">{clave.clave}</b>. Al entrar tendrá que cambiarla.</div>}
      <ul className="lista">{lista.map((t) => (
        <li key={t.id} className="fila" style={{ padding: "8px 4px", borderBottom: "1px solid var(--line)", justifyContent: "space-between", opacity: t.activo ? 1 : 0.6 }}>
          <span><b>{t.nombre}</b> {t.propietario && <span className="chip">Propietario</span>} {!t.activo && <span className="chip rojo">Desactivado</span>}<br /><small className="muted">{t.email}{t.ultimo_acceso ? ` · último acceso ${hora(t.ultimo_acceso)}` : " · aún no ha entrado"}</small></span>
          {!t.propietario && (
            <span className="fila" style={{ gap: 8 }}>
              <button className="enlace" onClick={async () => { const r = await a.correr(() => api<{ claveTemporal: string }>("POST", `/api/laboratorio/equipo/${t.id}/clave`)); if (r) setClave({ para: t.nombre, clave: r.claveTemporal }); }}>Restablecer contraseña</button>
              <button className="enlace" onClick={async () => { if (await a.correr(() => api("PUT", `/api/laboratorio/equipo/${t.id}`, { activo: !t.activo }))) await cargar(); }}>{t.activo ? "Desactivar" : "Reactivar"}</button>
            </span>
          )}
        </li>
      ))}</ul>
      <div className="fila" style={{ gap: 8, alignItems: "flex-end" }}>
        <label className="campo" style={{ flex: "1 1 200px" }}>Nombre del técnico<input value={nombre} maxLength={80} onChange={(e) => setNombre(e.target.value)} /></label>
        <label className="campo" style={{ flex: "1 1 220px" }}>Correo<input type="email" value={email} maxLength={120} onChange={(e) => setEmail(e.target.value)} /></label>
        <button className="btn pri" disabled={a.ocupado || nombre.trim().length < 3 || !email.trim()} onClick={async () => { const r = await a.correr(() => api<{ claveTemporal: string }>("POST", "/api/laboratorio/equipo", { nombre, email })); if (r) { setClave({ para: nombre, clave: r.claveTemporal }); setNombre(""); setEmail(""); await cargar(); } }}>Agregar técnico</button>
      </div>
    </Seccion>
  );
}

/** Lo único que ve el laboratorio al iniciar sesión: la bandeja con las órdenes de todas sus clínicas, sus clínicas, su equipo, su perfil y sus cuentas. */
export function PortalLaboratorio() {
  const [mi, setMi] = useState<LabFila | null>(null);
  const [error, setError] = useState<unknown>(null);
  const cargar = () => api<LabFila>("GET", "/api/laboratorio/mi").then(setMi).catch(setError);
  useEffect(() => { void cargar(); }, []);
  const [pestana, setPestana] = useState<"ordenes" | "clinicas" | "equipo" | "mi" | "cuenta">("ordenes");
  if (!mi) return <><InvitacionesRecibidas /><AvisoError error={error} /></>;
  const dueno = !!mi.propia && !!mi.propietario;
  return (
    <>
      <h1 className="titulo-pagina">Laboratorio · {mi.nombre}</h1>
      <InvitacionesRecibidas />
      <nav className="pestanas" role="tablist">
        {([["ordenes", "Bandeja de entrada", true], ["clinicas", "Mis clínicas", dueno], ["equipo", "Mi equipo", dueno], ["mi", "Mi laboratorio", true], ["cuenta", "Mis cuentas", true]] as [typeof pestana, string, boolean][])
          .filter((x) => x[2]).map(([k, t]) => <button key={k} role="tab" aria-selected={pestana === k} onClick={() => setPestana(k)}>{t}</button>)}
      </nav>
      {pestana === "ordenes" && <Bandeja />}
      {pestana === "clinicas" && dueno && <ClinicasLab />}
      {pestana === "equipo" && dueno && <EquipoLab />}
      {pestana === "mi" && (
        <Seccion titulo="Mi laboratorio">
          {mi.propia && !mi.propietario
            ? <p className="muted">Solo el propietario del laboratorio cambia el perfil. Trabajos que hace: {mi.tipos.length ? mi.tipos.map((t) => tipoTrabajo(t)?.nombre ?? t).join(", ") : "todos"}.</p>
            : <><p className="muted" style={{ margin: 0, fontSize: 13 }}>Elige con qué trabajos trabajas y personaliza tu hoja de datos: es lo que verá el odontólogo al pedirte un trabajo, en todas las clínicas con las que trabajas.</p><FormLab key={mi.id} inicial={mi} propio alGuardar={(l) => { setMi({ ...mi, ...l }); void cargar(); }} /></>}
        </Seccion>
      )}
      {pestana === "cuenta" && <Cuentas lado="laboratorio" />}
    </>
  );
}
